/** Form fields: a visible label, an inline hint before any error, one input style. */

import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { focusRing } from "./button";

export const inputClass = `block w-full rounded-lg bg-white px-3 py-2 text-sm text-zinc-900 shadow-xs ring-1 ring-zinc-300 placeholder:text-zinc-400 pointer-coarse:py-3 dark:bg-zinc-950 dark:text-zinc-100 dark:ring-zinc-700 dark:placeholder:text-zinc-500 disabled:bg-zinc-100 disabled:text-zinc-500 dark:disabled:bg-zinc-900 ${focusRing} focus-visible:ring-offset-0`;

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-zinc-800 dark:text-zinc-200">{label}</span>
      {children}
      {hint && <span className="block text-xs text-zinc-500 dark:text-zinc-400">{hint}</span>}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${inputClass} ${props.className ?? ""}`} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${inputClass} font-mono text-xs leading-5 ${props.className ?? ""}`} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${inputClass} pr-8 ${props.className ?? ""}`} />;
}

/** A borderless select for changing a value in place inside a table row. */
export function InlineSelect(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={`h-8 rounded-md bg-transparent pr-7 pl-2 text-sm text-zinc-700 hover:bg-zinc-100 disabled:opacity-50 disabled:hover:bg-transparent pointer-coarse:h-11 dark:text-zinc-300 dark:hover:bg-zinc-800 ${focusRing} focus-visible:ring-offset-0`}
    />
  );
}
